import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Mail, Link2, Plus, X, Send, Copy, Check } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Admin dialog to create/manage a client's portal link.
 * The portal lets the client (and trusted collaborators) qualify their leads
 * and add phone-call entries via a public URL secured by a 6-digit PIN sent
 * by email at each visit.
 */
export default function PortalClientDialog({ open, onOpenChange, client, onSaved }) {
  const [link, setLink] = useState(null);
  const [recipients, setRecipients] = useState([]);
  const [emailInput, setEmailInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [inviting, setInviting] = useState(false);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !client) return;
    setCopied(false);
    setLoading(true);
    axios.get(`${API}/client-portal/admin/clients/${client.id}/link`, { withCredentials: true })
      .then(({ data }) => {
        if (data.exists) {
          setLink(data);
          setRecipients(data.recipients || []);
        } else {
          setLink(null);
          setRecipients([]);
        }
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Erreur'))
      .finally(() => setLoading(false));
  }, [open, client]);

  const addEmail = () => {
    const e = (emailInput || '').trim().toLowerCase();
    if (!e || !e.includes('@')) {
      toast.error('Email invalide');
      return;
    }
    if (recipients.includes(e)) return;
    if (recipients.length >= 10) {
      toast.error('Maximum 10 destinataires');
      return;
    }
    setRecipients([...recipients, e]);
    setEmailInput('');
  };

  const removeEmail = (e) => setRecipients(recipients.filter(x => x !== e));

  const save = async () => {
    if (!client) return;
    setSaving(true);
    try {
      const { data } = await axios.post(
        `${API}/client-portal/admin/clients/${client.id}/link`,
        { recipients },
        { withCredentials: true },
      );
      setLink(data);
      toast.success('Portail enregistré');
      onSaved?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  const sendInvite = async () => {
    if (!client) return;
    if (!recipients.length) {
      toast.error('Ajoutez au moins un destinataire');
      return;
    }
    setInviting(true);
    try {
      const { data } = await axios.post(
        `${API}/client-portal/admin/clients/${client.id}/link/invite`,
        {},
        { withCredentials: true },
      );
      toast.success(`Invitation envoyée à ${data.count} destinataire(s)`);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur envoi');
    } finally {
      setInviting(false);
    }
  };

  const copyUrl = async () => {
    if (!link?.portal_url) return;
    try {
      await navigator.clipboard.writeText(link.portal_url);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      toast.error('Copie impossible — sélectionnez et copiez manuellement');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); addEmail(); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-xl" data-testid="portal-client-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Link2 className="w-5 h-5 text-[#002FA7]" />
            Portail client — {client?.name}
          </DialogTitle>
          <DialogDescription className="text-xs">
            Génère une URL publique où le client peut qualifier ses leads et ajouter ses appels reçus.
            Chaque visite déclenche un code à 6 chiffres envoyé par email.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="text-center text-neutral-400 text-sm py-6">Chargement…</div>
        ) : (
          <div className="space-y-5 mt-2">
            {/* URL display */}
            {link?.portal_url && (
              <div className="space-y-1.5">
                <Label className="text-xs">URL publique du portail</Label>
                <div className="flex gap-2">
                  <input
                    readOnly
                    value={link.portal_url}
                    className="flex-1 rounded-md border border-neutral-200 bg-neutral-50 px-3 py-2 text-xs font-mono text-neutral-700"
                    data-testid="portal-url-input"
                    onFocus={(e) => e.target.select()}
                  />
                  <Button onClick={copyUrl} variant="outline" size="sm" data-testid="copy-portal-url">
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-[11px] text-neutral-400">
                  À envoyer manuellement OU via le bouton "Envoyer l'invitation" plus bas.
                </p>
              </div>
            )}

            {/* Recipients */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-xs">Destinataires autorisés ({recipients.length}/10)</Label>
                <span className="text-[10px] text-neutral-400">recevront le code à chaque connexion</span>
              </div>
              <div className="flex gap-2">
                <input
                  type="email"
                  value={emailInput}
                  onChange={(e) => setEmailInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  placeholder="email@exemple.com"
                  className="flex-1 rounded-md border border-neutral-200 px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#002FA7]"
                  data-testid="portal-email-input"
                />
                <Button onClick={addEmail} variant="outline" size="sm" disabled={!emailInput.trim()}>
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1.5">
                {recipients.length === 0 ? (
                  <span className="text-xs text-neutral-400 italic">Aucun destinataire — ajoutez au moins une adresse pour activer le portail.</span>
                ) : (
                  recipients.map(e => (
                    <span key={e} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded bg-blue-50 text-blue-700 border border-blue-200" data-testid={`recipient-${e}`}>
                      <Mail className="w-3 h-3" />
                      {e}
                      <button onClick={() => removeEmail(e)} className="hover:text-red-600">
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap justify-end gap-2 pt-3 border-t border-neutral-200">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Fermer</Button>
              <Button
                onClick={save}
                disabled={saving}
                className="bg-[#002FA7] hover:bg-[#002277]"
                data-testid="save-portal-btn"
              >
                {saving ? 'Enregistrement…' : 'Enregistrer'}
              </Button>
              {link?.portal_url && (
                <Button
                  onClick={sendInvite}
                  disabled={inviting || !recipients.length}
                  variant="default"
                  className="bg-emerald-600 hover:bg-emerald-700"
                  data-testid="send-portal-invite"
                  title="Envoyer un email d'invitation contenant l'URL du portail"
                >
                  <Send className="w-4 h-4 mr-1" />
                  {inviting ? 'Envoi…' : "Envoyer l'invitation"}
                </Button>
              )}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
